import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TActivateEUDdpDialogProps, TActivateEUDdpFormValues } from './types';
import { ACTIVATE_EU_DDP_PRICING_PLANS_PAGE_SIZE } from './constants';
import { getActivateEUDdpInitialValues } from './utils';
import { CountrySelectMenu } from 'ui/CountrySelectMenu';
import { ESortDirection, ETableStructureType } from 'ui/EasTable/types';
import { searchPricingPlansRequest } from 'redux/reducers/euDdp/reducer';
import { getCountryListData } from 'redux/reducers/country/selectors';
import {
	selectEuDdpRecordsSearch,
	selectPricingPlansSearchData,
	selectPricingPlansSearchLoading,
} from 'redux/reducers/euDdp/selectors';
import { notificationContainer } from '../../../../utils/notificationContainer';
import {
	activateEuDdpRequest,
	resetActivateEuDdp,
} from '../../../../redux/reducers/emManagement/reducer';
import {
	getActivateEuDdpLoading,
	getActivateEuDdpSuccess,
	getEmByIdFull,
} from '../../../../redux/reducers/emManagement/selectors';

const validationSchema = Yup.object({
	region: Yup.string().trim().required('Region is required'),
	postalCode: Yup.string().trim().required('Postal code is required'),
	streetNo: Yup.string().trim().required('Street and building is required'),
	city: Yup.string().trim().required('City is required'),
	country: Yup.number().required('Country is required').typeError('Country is required'),
	pricingPlanId: Yup.number()
		.required('Pricing plan is required')
		.typeError('Pricing plan is required'),
	personalFixedFee: Yup.number()
		.min(0, 'Must be greater than or equal to 0')
		.required('Personal fixed fee is required'),
	personalPercentageFee: Yup.number()
		.min(0, 'Must be greater than or equal to 0')
		.max(100, 'Must be less than or equal to 100')
		.required('Personal percentage fee is required'),
});

export const ActivateEUDdpDialog = ({
	open,
	onClose,
	// emData,
	// onSubmit,
	// activateEuDdpLoading = false,
}: TActivateEUDdpDialogProps) => {
	const dispatch = useDispatch();
	const countryList = useSelector(getCountryListData);
	const pricingPlans = useSelector(selectPricingPlansSearchData);
	const pricingPlansLoading = useSelector(selectPricingPlansSearchLoading);
	const activateEuDdpLoading = useSelector(getActivateEuDdpLoading);
	const activateEuDdpSuccess = useSelector(getActivateEuDdpSuccess);
	const euDdpRecords = useSelector(selectEuDdpRecordsSearch);
	const emData = useSelector(getEmByIdFull);
	const euDdpRecordCandidate = euDdpRecords?.data?.[0];
	const euDdpRecord =
		emData?.id != null && euDdpRecordCandidate && euDdpRecordCandidate.emId === emData.id
			? euDdpRecordCandidate
			: undefined;

	const handleActivateEUDdp = (values: TActivateEUDdpFormValues) => {
		if (values.country == null) {
			notificationContainer('Please select a country', 'error');
			return;
		}
		const countryItem = countryList?.find((c) => c.id === values.country);
		const countryCode = countryItem?.isoAlpha2;
		if (!countryCode) {
			notificationContainer('Please select a valid country', 'error');
			return;
		}
		dispatch(
			activateEuDdpRequest({
				emId: Number(emData?.id),
				data: {
					streetNo: values.streetNo,
					region: values.region,
					city: values.city,
					postalCode: values.postalCode,
					country: countryCode,
					pricingPlanId: values.pricingPlanId,
					personalFixedFee: values.personalFixedFee,
					personalPercentageFee: values.personalPercentageFee,
					postPaymentAllowed: values.postPaymentAllowed,
				},
			}),
		);
	};

	useEffect(() => {
		if (open) {
			dispatch(
				searchPricingPlansRequest({
					pageNumber: 1,
					pageSize: ACTIVATE_EU_DDP_PRICING_PLANS_PAGE_SIZE,
					searchString: '',
					orderBy: [{ fieldName: 'id', direction: ESortDirection.DESC }],
				}),
			);
		}
	}, [open, dispatch]);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: getActivateEUDdpInitialValues(emData, euDdpRecord, countryList),
		validationSchema,
		onSubmit: (values) => {
			handleActivateEUDdp(values);
		},
	});

	const selectedPricingPlan =
		pricingPlans.find((plan) => plan.id === formik.values.pricingPlanId) ?? null;

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	const handleCloseRef = useRef(handleClose);
	useEffect(() => {
		handleCloseRef.current = handleClose;
	});

	const prevActivateEuDdpLoadingRef = useRef(activateEuDdpLoading);
	useEffect(() => {
		const wasLoading = prevActivateEuDdpLoadingRef.current;
		prevActivateEuDdpLoadingRef.current = activateEuDdpLoading;
		if (open && wasLoading && !activateEuDdpLoading && activateEuDdpSuccess) {
			dispatch(resetActivateEuDdp());
			handleCloseRef.current();
		}
	}, [open, activateEuDdpLoading, activateEuDdpSuccess, dispatch]);

	return (
		<Dialog
			open={open}
			onClose={activateEuDdpLoading ? () => {} : handleClose}
			fullWidth
			maxWidth="sm"
			PaperProps={{
				sx: { borderRadius: '12px' },
			}}
		>
			<DialogTitle color="primary">
				{(euDdpRecords?.data?.[0]?.ddpEnabled ?? '') ? 'Edit EU DDP' : 'Activate EU DDP'}
			</DialogTitle>
			<form onSubmit={formik.handleSubmit}>
				<DialogContent>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
						<TextField
							name="region"
							label="Region"
							size="small"
							fullWidth
							value={formik.values.region}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={activateEuDdpLoading}
							error={formik.touched.region && Boolean(formik.errors.region)}
							helperText={formik.touched.region && formik.errors.region}
						/>
						<TextField
							name="postalCode"
							label="Postal code"
							size="small"
							fullWidth
							value={formik.values.postalCode}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={activateEuDdpLoading}
							error={formik.touched.postalCode && Boolean(formik.errors.postalCode)}
							helperText={formik.touched.postalCode && formik.errors.postalCode}
						/>
						<TextField
							name="streetNo"
							label="Street and building"
							size="small"
							fullWidth
							value={formik.values.streetNo}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={activateEuDdpLoading}
							error={formik.touched.streetNo && Boolean(formik.errors.streetNo)}
							helperText={formik.touched.streetNo && formik.errors.streetNo}
						/>
						<TextField
							name="city"
							label="City"
							size="small"
							fullWidth
							value={formik.values.city}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							disabled={activateEuDdpLoading}
							error={formik.touched.city && Boolean(formik.errors.city)}
							helperText={formik.touched.city && formik.errors.city}
						/>
						<Box
							sx={{
								opacity: activateEuDdpLoading ? 0.6 : 1,
								pointerEvents: activateEuDdpLoading ? 'none' : 'auto',
							}}
						>
							<CountrySelectMenu
								label="Country"
								type={ETableStructureType.COUNTRY_NUMBER}
								countryNumber={formik.values.country}
								onChangeNumber={(countryNumber) => {
									formik.setFieldValue('country', countryNumber).catch(() => {});
								}}
								onBlur={() => {
									void formik.setFieldTouched('country', true);
								}}
								size="small"
								error={formik.touched.country && Boolean(formik.errors.country)}
								helperText={formik.touched.country && formik.errors.country}
							/>
						</Box>
						<Autocomplete
							options={pricingPlans}
							loading={pricingPlansLoading}
							disabled={activateEuDdpLoading}
							getOptionLabel={(option) => option.displayName}
							value={selectedPricingPlan}
							onChange={(_event, option) => {
								void formik.setFieldValue('pricingPlanId', option?.id ?? null);
								if (option) {
									void formik.setFieldValue('personalFixedFee', option.fixedFee);
									void formik.setFieldValue('personalPercentageFee', option.percentageFee);
								} else {
									void formik.setFieldValue('personalFixedFee', 0);
									void formik.setFieldValue('personalPercentageFee', 0);
								}
							}}
							onBlur={() => {
								void formik.setFieldTouched('pricingPlanId', true);
							}}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Pricing plan"
									name="pricingPlanId"
									size="small"
									error={formik.touched.pricingPlanId && Boolean(formik.errors.pricingPlanId)}
									helperText={formik.touched.pricingPlanId && formik.errors.pricingPlanId}
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{pricingPlansLoading ? (
													<CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
												) : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
						/>
						<TextField
							name="personalFixedFee"
							label="Personal fixed fee"
							size="small"
							fullWidth
							type="number"
							inputProps={{ min: 0, step: 'any' }}
							value={formik.values.personalFixedFee}
							onChange={(e) => {
								const raw = e.target.value;
								void formik.setFieldValue('personalFixedFee', raw === '' ? 0 : Number(raw));
							}}
							onBlur={formik.handleBlur}
							disabled={activateEuDdpLoading}
							error={formik.touched.personalFixedFee && Boolean(formik.errors.personalFixedFee)}
							helperText={formik.touched.personalFixedFee && formik.errors.personalFixedFee}
						/>
						<TextField
							name="personalPercentageFee"
							label="Personal percentage fee"
							size="small"
							fullWidth
							type="number"
							inputProps={{ min: 0, max: 100, step: 'any' }}
							value={formik.values.personalPercentageFee}
							onChange={(e) => {
								const raw = e.target.value;
								void formik.setFieldValue('personalPercentageFee', raw === '' ? 0 : Number(raw));
							}}
							onBlur={formik.handleBlur}
							disabled={activateEuDdpLoading}
							error={
								formik.touched.personalPercentageFee && Boolean(formik.errors.personalPercentageFee)
							}
							helperText={
								formik.touched.personalPercentageFee && formik.errors.personalPercentageFee
							}
						/>
						<FormControlLabel
							control={
								<Checkbox
									name="postPaymentAllowed"
									checked={formik.values.postPaymentAllowed}
									onChange={formik.handleChange}
									disabled={activateEuDdpLoading}
								/>
							}
							label="Post-payment is allowed"
						/>
					</Box>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={handleClose} disabled={activateEuDdpLoading}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" color="primary" disabled={activateEuDdpLoading}>
						{activateEuDdpLoading ? (
							<CircularProgress size={22} color="inherit" />
						) : (euDdpRecords?.data?.[0]?.ddpEnabled ?? '') ? (
							'Edit'
						) : (
							'Activate'
						)}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
